import { useEffect, useId, useRef, useState } from 'react';
import OverflowMenu from '../../components/OverflowMenu';
import { useAnchoredMenuPosition } from '../../components/useAnchoredMenuPosition';
import {
  SocialBookmarkIcon,
  SocialCommentIcon,
  SocialHeartIcon,
  SocialMoreIcon,
  SocialRepostIcon,
} from '../social-post/SocialActionIcons';
import { useTravelText } from '../../app/TravelLocaleContext';
import { formatCount } from '../../i18n/travelFormat';
import '../social-post/post-engagement-bar.css';

const E = 'travel.publicJourney.engagement';

/**
 * 公開行程社群操作列：按讚 → 留言 → 分享→ 收藏 → 更多。
 * 不捏造 comment／share／share 計數；更多選單依能力顯示。
 */
export default function PublicJourneyEngagementBar({
  engagement,
  busy = false,
  onToggleLiked,
  onToggleSaved,
  onOpenComments,
  moreItems = [],
  className = '',
}) {
  const { t, intlLocale } = useTravelText();
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [copyNotice, setCopyNotice] = useState('');
  const shareWrapRef = useRef(null);
  const shareTriggerRef = useRef(null);
  const shareMenuRef = useRef(null);
  const shareMenuId = useId();
  const shareMenuStyle = useAnchoredMenuPosition(shareMenuOpen, shareTriggerRef, shareMenuRef);

  const liked = Boolean(engagement?.liked);
  const saved = Boolean(engagement?.saved);
  const likeCount = engagement ? (Number(engagement.likeCount) || 0) : null;
  const saveCount = engagement ? (Number(engagement.saveCount) || 0) : null;
  const locked = Boolean(busy);

  useEffect(() => {
    if (!shareMenuOpen) return undefined;
    function onPointerDown(event) {
      if (shareWrapRef.current && !shareWrapRef.current.contains(event.target)) {
        setShareMenuOpen(false);
      }
    }
    function onEscape(event) {
      if (event.key === 'Escape') setShareMenuOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [shareMenuOpen]);

  function handleMoreMenuOpenChange(next) {
    if (next && locked) return;
    if (next) setShareMenuOpen(false);
    setMoreMenuOpen(next);
  }

  async function copyLink() {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      setCopyNotice(`${E}.copied`);
      window.setTimeout(() => setCopyNotice(''), 2400);
    } catch {
      setCopyNotice(`${E}.copyFailed`);
      window.setTimeout(() => setCopyNotice(''), 2400);
    }
  }

  const resolvedMoreItems = [
    ...moreItems,
    { id: 'copy-link', label: t(`${E}.copyLink`), onSelect: copyLink, testId: 'public-journey-copy-link' },
  ];

  return (
    <>
      <footer
        className={`post-engagement-bar journey-engagement-bar ${className}`.trim()}
        data-testid="public-journey-engagement"
        aria-label={t(`${E}.label`)}
      >
        <button
          type="button"
          className="post-engagement-bar__action"
          data-testid="public-journey-like"
          aria-label={t(liked ? 'travel.publicJourney.unlike' : 'travel.publicJourney.like')}
          aria-pressed={liked}
          disabled={locked}
          onClick={onToggleLiked}
        >
          <SocialHeartIcon className="post-engagement-bar__icon" filled={liked} />
          {likeCount != null ? <span className="post-engagement-bar__count">{formatCount(likeCount, intlLocale)}</span> : null}
        </button>

        <button
          type="button"
          onClick={onOpenComments}
          aria-controls="journey-comments"
          className="post-engagement-bar__action post-engagement-bar__comment"
          data-testid="public-journey-comment"
          aria-label={t(`${E}.comments`)}
        >
          <SocialCommentIcon className="post-engagement-bar__icon" />
        </button>

        <div className="post-engagement-bar__menu-wrap" ref={shareWrapRef}>
          <button
            ref={shareTriggerRef}
            type="button"
            className="post-engagement-bar__action"
            data-testid="public-journey-share"
            aria-label={t(`${E}.shareMenu`)}
            aria-expanded={shareMenuOpen}
            aria-controls={shareMenuId}
            disabled={locked}
            onClick={() => {
              if (locked) return;
              setMoreMenuOpen(false);
              setShareMenuOpen((open) => !open);
            }}
          >
            <SocialRepostIcon className="post-engagement-bar__icon" />
          </button>
          {shareMenuOpen ? (
            <div
              id={shareMenuId}
              ref={shareMenuRef}
              className="post-engagement-bar__menu"
              role="menu"
              data-testid="public-journey-share-menu"
              style={shareMenuStyle}
            >
              <button type="button" role="menuitem" onClick={() => { setShareMenuOpen(false); copyLink(); }}>
                {t(`${E}.copyLink`)}
              </button>
              {moreItems.filter(item => item.id === 'share-im').map(item => (
                <button key={item.id} type="button" role="menuitem" data-testid={item.testId}
                  onClick={() => { setShareMenuOpen(false); item.onSelect(); }}>{item.label}</button>
              ))}
            </div>
          ) : null}
        </div>

        <button
          type="button"
          className="post-engagement-bar__action"
          data-testid="public-journey-save"
          aria-label={t(saved ? 'travel.publicJourney.unsave' : 'travel.publicJourney.save')}
          aria-pressed={saved}
          disabled={locked}
          onClick={onToggleSaved}
        >
          <SocialBookmarkIcon className="post-engagement-bar__icon" filled={saved} />
          {saveCount != null ? <span className="post-engagement-bar__count">{formatCount(saveCount, intlLocale)}</span> : null}
        </button>

        <OverflowMenu
          items={resolvedMoreItems}
          label={t(`${E}.more`)}
          triggerContent={<SocialMoreIcon className="post-engagement-bar__icon" />}
          disabled={locked}
          anchored
          sheetOnNarrow
          open={moreMenuOpen}
          onOpenChange={handleMoreMenuOpenChange}
          wrapClassName="post-engagement-bar__menu-wrap"
          triggerClassName="post-engagement-bar__action"
          menuClassName="post-engagement-bar__menu"
          triggerTestId="public-journey-more"
          menuTestId="public-journey-more-menu"
        />
      </footer>
      {copyNotice ? (
        <p className="journey-engagement-bar__notice" role="status">{t(copyNotice)}</p>
      ) : null}
    </>
  );
}
